import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ADMIN_API_PREFIX, BASE_URL } from '@CONSTANTS/API.constant';
import userRepository from '@REPOSITORIES/local-repository/user-repository';
import { sectionsAdminApi } from './sections.admin-api';

type AdminVendors = {
    data: AdminVendorsData
};

type AdminVendorsData = AdminVendor[];

export type AdminVendor = {
    id: number;
    name: string;
    company: string;
    address: string;
    email: string;
    phone: string;
    isVisible: boolean;
    products: AdminVendorProduct[];
    usage_locations?: VendorUsageLocation[];
    contactPerson?: string;
    website?: string;
    status?: string;
    contractStatus?: string;
    notes?: string;
};

export type VendorUsageLocation = {
    type: 'section' | 'variant';
    document_id: number;
    document_name: string;
    chapter_id: number;
    chapter_name: string;
    section_id: number;
    section_name: string;
    variant_id?: number;
    variant_name?: string;
    location_path: string;
};

type AdminVendorProduct = {
    id: number;
    name: string;
};

type GetAllAdminApiResponse = {
    id: number;
    name: string;
    company: string;
    address: string;
    email: string;
    phone: string;
    is_visible: boolean;
    contact_person?: string;
    website?: string;
    status?: string;
    contract_status?: string;
    notes?: string;
    products: {
        id: number;
        name: string;
    }[];
    usage_locations?: {
        type: 'section' | 'variant';
        document_id: number;
        document_name: string;
        chapter_id: number;
        chapter_name: string;
        section_id: number;
        section_name: string;
        variant_id?: number;
        variant_name?: string;
        location_path: string;
    }[];
};

type CreateParams = {
    name: string;
    company: string;
    address?: string;
    email?: string;
    phone?: string;
    isVisible: boolean;
    contactPerson?: string;
    website?: string;
    status?: string;
    contractStatus?: string;
    notes?: string;
};

type UpdateParams = {
    id: number;
    name: string;
    company: string;
    address?: string;
    email?: string;
    phone?: string;
    isVisible: boolean;
    contactPerson?: string;
    website?: string;
    status?: string;
    contractStatus?: string;
    notes?: string;
};

type DeleteParams = {
    id: number;
};

type DownloadTemplateParams = {
    include_sample_data?: boolean;
};

type ImportVendorsParams = {
    file: File;
    workspace_id?: number;
};

type ImportResponse = {
    success: boolean;
    imported: number;
    errors: ImportError[];
    warnings: ImportWarning[];
    message: string;
};

type ImportError = {
    row: number;
    field: string;
    message: string;
};

type ImportWarning = {
    row: number;
    field: string;
    message: string;
};

function transformGetAllResponse(raw: GetAllAdminApiResponse[]): AdminVendors {
    return {
        data: raw.map((vendor) => ({
            id: vendor.id,
            name: vendor.name,
            company: vendor.company,
            address: vendor.address,
            email: vendor.email,
            phone: vendor.phone,
            isVisible: vendor.is_visible,
            contactPerson: vendor.contact_person,
            website: vendor.website,
            status: vendor.status,
            contractStatus: vendor.contract_status,
            notes: vendor.notes,
            products: vendor.products.map((product) => ({
                id: product.id,
                name: product.name,
            })),
            usage_locations: vendor.usage_locations || [],
        })),
    };
}

export const vendorsAdminApi = createApi({
    reducerPath: 'vendors-admin-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + ADMIN_API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    endpoints: (builder) => ({
        getAll: builder.query<AdminVendors, void>({
            query: () => ({
                url: '/vendors/',
                method: 'GET',
            }),
            transformResponse(raw: GetAllAdminApiResponse[]) {
                return transformGetAllResponse(raw);
            },
        }),
        create: builder.mutation<GetAllAdminApiResponse, CreateParams>({
            query: (params) => {
                const formData = new FormData();

                formData.append('name', params.name);
                formData.append('company', params.company);
                if (params.address !== undefined) {
                    formData.append('address', params.address);
                }
                if (params.email !== undefined) {
                    formData.append('email', params.email);
                }
                if (params.phone !== undefined) {
                    formData.append('phone', params.phone);
                }
                formData.append('is_visible', params.isVisible.toString());

                // Add new optional fields
                if (params.contactPerson !== undefined) {
                    formData.append('contact_person', params.contactPerson);
                }
                if (params.website !== undefined) {
                    formData.append('website', params.website);
                }
                if (params.status !== undefined) {
                    formData.append('status', params.status);
                }
                if (params.contractStatus !== undefined) {
                    formData.append('contract_status', params.contractStatus);
                }
                if (params.notes !== undefined) {
                    formData.append('notes', params.notes);
                }

                return ({
                    url: '/vendors/',
                    method: 'POST',
                    body: formData,
                });
            },
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data: queryData } = await queryFulfilled;

                    // INVALIDATE DATA
                    dispatch(
                        sectionsAdminApi.util.resetApiState(),
                    );

                    // ADD VENDOR TO VENDORS PAGE
                    dispatch(
                        vendorsAdminApi.util.updateQueryData('getAll', undefined, (draft) => ({
                            data: [...draft.data, {
                                id: queryData.id,
                                name: queryData.name,
                                company: queryData.company,
                                address: queryData.address,
                                email: queryData.email,
                                phone: queryData.phone,
                                isVisible: queryData.is_visible,
                                contactPerson: queryData.contact_person,
                                website: queryData.website,
                                status: queryData.status,
                                contractStatus: queryData.contract_status,
                                notes: queryData.notes,
                                products: [],
                                usage_locations: [],
                            }],
                        })),
                    );
                } catch (e) {
                    //
                }
            },
        }),
        update: builder.mutation<GetAllAdminApiResponse, UpdateParams>({
            query: (params) => {
                const formData = new FormData();

                formData.append('name', params.name);
                formData.append('company', params.company);
                if (params.address !== undefined) {
                    formData.append('address', params.address);
                }
                if (params.email !== undefined) {
                    formData.append('email', params.email);
                }
                if (params.phone !== undefined) {
                    formData.append('phone', params.phone);
                }
                formData.append('is_visible', params.isVisible.toString());

                // Add new optional fields
                if (params.contactPerson !== undefined) {
                    formData.append('contact_person', params.contactPerson);
                }
                if (params.website !== undefined) {
                    formData.append('website', params.website);
                }
                if (params.status !== undefined) {
                    formData.append('status', params.status);
                }
                if (params.contractStatus !== undefined) {
                    formData.append('contract_status', params.contractStatus);
                }
                if (params.notes !== undefined) {
                    formData.append('notes', params.notes);
                }

                return ({
                    url: `/vendors/${params.id}/`,
                    method: 'PATCH',
                    body: formData,
                });
            },
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                try {
                    const { data: queryData } = await queryFulfilled;

                    // INVALIDATE DATA
                    dispatch(
                        sectionsAdminApi.util.resetApiState(),
                    );

                    // UPDATE VENDOR IN VENDORS PAGE
                    dispatch(
                        vendorsAdminApi.util.updateQueryData('getAll', undefined, (draft) => ({
                            data: draft.data.map((vendor) => {
                                if (vendor.id === id) {
                                    return {
                                        ...vendor,
                                        name: queryData.name,
                                        company: queryData.company,
                                        address: queryData.address,
                                        email: queryData.email,
                                        phone: queryData.phone,
                                        isVisible: queryData.is_visible,
                                        contactPerson: queryData.contact_person,
                                        website: queryData.website,
                                        status: queryData.status,
                                        contractStatus: queryData.contract_status,
                                        notes: queryData.notes,
                                    };
                                }

                                return vendor;
                            }),
                        })),
                    );
                } catch (e) {
                    //
                }
            },
        }),
        delete: builder.mutation<void, DeleteParams>({
            query: (params) => ({
                url: `/vendors/${params.id}/`,
                method: 'DELETE',
            }),
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    // INVALIDATE DATA
                    dispatch(
                        sectionsAdminApi.util.resetApiState(),
                    );

                    // DELETE VENDOR FROM VENDORS PAGE
                    dispatch(
                        vendorsAdminApi.util.updateQueryData('getAll', undefined, (draft) => ({
                            data: draft.data.filter((vendor) => vendor.id !== id),
                        })),
                    );
                } catch (e) {
                    //
                }
            },
        }),
        downloadTemplate: builder.mutation<Blob, DownloadTemplateParams>({
            query: (params) => {
                const searchParams = new URLSearchParams();
                if (params.include_sample_data !== undefined) {
                    searchParams.append('include_sample_data', params.include_sample_data.toString());
                }

                return {
                    url: `/vendors/template/?${searchParams.toString()}`,
                    method: 'GET',
                    responseHandler: async (response) => {
                        if (!response.ok) {
                            // Handle error responses properly to avoid blob serialization issues
                            let errorMessage = `HTTP ${response.status}`;
                            try {
                                const errorText = await response.text();
                                errorMessage = errorText || errorMessage;
                            } catch {
                                // If we can't read the error text, use the status
                                errorMessage = `${response.status} ${response.statusText}`;
                            }
                            throw new Error(errorMessage);
                        }
                        return response.blob();
                    },
                };
            },
        }),
        importVendors: builder.mutation<ImportResponse, ImportVendorsParams>({
            query: (params) => {
                const formData = new FormData();
                formData.append('file', params.file);
                
                if (params.workspace_id !== undefined) {
                    formData.append('workspace_id', params.workspace_id.toString());
                }

                return {
                    url: '/vendors/import/',
                    method: 'POST',
                    body: formData,
                };
            },
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    // INVALIDATE DATA to refresh vendors list
                    dispatch(
                        vendorsAdminApi.util.resetApiState(),
                    );
                } catch (e) {
                    //
                }
            },
        }),
    }),
});

export const {
    useGetAllQuery: useGetAllVendors,
    useCreateMutation: useCreateVendor,
    useUpdateMutation: useUpdateVendor,
    useDeleteMutation: useDeleteVendor,
    useDownloadTemplateMutation: useDownloadTemplate,
    useImportVendorsMutation: useImportVendors,
} = vendorsAdminApi;
